import * as signalR from '@microsoft/signalr';
import { getChatToken } from '../ApiClientArchitecture/tokenStore';
import { isUnauthorizedError, notifySessionExpired } from '../ApiClientArchitecture/session';

/**
 * Lifecycle notifications for the chat hub connection.
 * - `connected`: a (re)started connection is up (initial start or a retry after a failed start / close)
 * - `reconnecting`: the automatic reconnect loop is running; messages may be missed
 * - `reconnected`: the automatic reconnect loop succeeded; missed messages must be recovered
 * - `disconnected`: the connection is down and not currently reconnecting by itself
 */
export type ConnectionEvent = 'connected' | 'reconnecting' | 'reconnected' | 'disconnected';

type ConnectionParams = { tenantId: string; userId: string; token: string };

let connection: signalR.HubConnection | null = null;
let connectionParams: ConnectionParams | null = null;
let startPromise: Promise<void> | null = null;
let receiveCallback: ((payload: any) => void) | null = null;
let draftCallback: ((payload: any) => void) | null = null;
const eventListeners = new Set<(event: ConnectionEvent) => void>();
let retryCount = 0;
let retryTimer: ReturnType<typeof setTimeout> | null = null;
const MAX_RETRY_ATTEMPTS = 5;
const RETRY_DELAY = 5000;

const emit = (event: ConnectionEvent) => {
   eventListeners.forEach((listener) => {
      try {
         listener(event);
      } catch (err) {
         console.error('SignalR connection listener failed:', err);
      }
   });
};

const clearRetryTimer = () => {
   if (retryTimer) {
      clearTimeout(retryTimer);
      retryTimer = null;
   }
};

const scheduleRetry = () => {
   clearRetryTimer();
   if (!connectionParams) return;

   if (retryCount >= MAX_RETRY_ATTEMPTS) {
      console.error('Max retry attempts reached. Stopping connection attempts.');
      retryCount = 0;
      return;
   }

   retryCount++;
   console.log(`Retrying connection in ${RETRY_DELAY}ms (attempt ${retryCount}/${MAX_RETRY_ATTEMPTS})`);
   const params = connectionParams;
   retryTimer = setTimeout(() => {
      retryTimer = null;
      startConnection(params.tenantId, params.userId, params.token).catch(() => {
         // failure already reported and the next retry scheduled
      });
   }, RETRY_DELAY);
};

// Create a connection with the correct tenant and user context. Handlers are
// attached to every connection instance, so a retry never loses the
// ReceiveMessage / ReceiveMessageDraft listeners, and events from a superseded instance are ignored.
const createConnection = ({ tenantId, userId, token }: ConnectionParams): signalR.HubConnection => {
   const conn = new signalR.HubConnectionBuilder()
      .withUrl(`${process.env.REACT_APP_BASE_URL}/chathub?tenantId=${encodeURIComponent(tenantId)}&userId=${encodeURIComponent(userId)}`, {
         // Read at every (re)connect so a refreshed token is used without restarting the page.
         accessTokenFactory: () => getChatToken() || token
      })
      .withAutomaticReconnect()
      .withServerTimeout(90000)
      .configureLogging(signalR.LogLevel.Information)
      .build();

   conn.on('ReceiveMessage', (payload: unknown) => {
      if (conn === connection) receiveCallback?.(payload);
   });

   // The answer so far, while the concierge is still writing it (optional: the API may never send one).
   conn.on('ReceiveMessageDraft', (payload: unknown) => {
      if (conn === connection) draftCallback?.(payload);
   });

   conn.onreconnecting((error) => {
      if (conn !== connection) return;
      console.warn('SignalR reconnecting...', error);
      emit('reconnecting');
   });

   conn.onreconnected(() => {
      if (conn !== connection) return;
      console.log('SignalR reconnected.');
      emit('reconnected');
   });

   conn.onclose((error) => {
      if (conn !== connection) return;
      console.warn('SignalR connection closed.', error);
      emit('disconnected');
      scheduleRetry();
   });

   return conn;
};

const teardown = async (): Promise<void> => {
   clearRetryTimer();
   const conn = connection;
   connection = null;
   startPromise = null;
   if (conn && conn.state !== signalR.HubConnectionState.Disconnected) {
      try {
         await conn.stop();
         console.log('SignalR disconnected.');
      } catch (error) {
         console.error('Error stopping SignalR connection:', error);
      }
   }
};

// Start or reuse the connection. Rejects when this attempt fails; retries continue in the background.
export const startConnection = async (tenantId: string, userId: string, token: string): Promise<void> => {
   // Validate required parameters before attempting connection
   if (!tenantId || !userId || !token || token.trim() === '') {
      console.log('Missing required connection parameters (tenantId, userId, or token), skipping connection');
      return;
   }

   const sameParams = connectionParams?.tenantId === tenantId && connectionParams?.userId === userId && connectionParams?.token === token;
   connectionParams = { tenantId, userId, token };

   if (sameParams && connection) {
      if (connection.state === signalR.HubConnectionState.Connected) return; // already connected
      if (startPromise) return startPromise; // start in progress
      if (connection.state === signalR.HubConnectionState.Reconnecting) return; // automatic reconnect in progress
   }

   await teardown();

   const conn = createConnection(connectionParams);
   connection = conn;

   const attempt = conn
      .start()
      .then(() => {
         if (conn !== connection) return;
         console.log('SignalR connected.');
         retryCount = 0; // Reset retry count on successful connection
         emit('connected');
      })
      .catch((err) => {
         if (conn !== connection) return;
         console.error('SignalR connection error:', err);
         emit('disconnected');
         if (isUnauthorizedError(err)) {
            // Retrying with a rejected token cannot succeed: end the session instead.
            connectionParams = null;
            retryCount = 0;
            notifySessionExpired();
         } else {
            scheduleRetry();
         }
         throw err;
      })
      .finally(() => {
         if (startPromise === attempt) startPromise = null;
      });

   startPromise = attempt;
   return attempt;
};

/**
 * Restarts the connection (with the last known parameters) if it is down,
 * e.g. when the tab becomes visible again after the retries gave up.
 */
export const ensureConnected = (): void => {
   if (!connectionParams) return;
   const state = connection?.state;
   if (
      state === signalR.HubConnectionState.Connected ||
      state === signalR.HubConnectionState.Connecting ||
      state === signalR.HubConnectionState.Reconnecting ||
      startPromise
   ) {
      return;
   }
   retryCount = 0;
   const { tenantId, userId, token } = connectionParams;
   startConnection(tenantId, userId, token).catch(() => {
      // reported via the 'disconnected' event; retries continue in the background
   });
};

export const addReceiveMessageListener = <T = unknown>(callback: (payload: T) => void): void => {
   receiveCallback = callback as (payload: any) => void;
};

export const removeReceiveMessageListener = (): void => {
   receiveCallback = null;
};

export const addReceiveDraftListener = <T = unknown>(callback: (payload: T) => void): void => {
   draftCallback = callback as (payload: any) => void;
};

export const removeReceiveDraftListener = (): void => {
   draftCallback = null;
};

/** Subscribe to connection lifecycle events. Returns an unsubscribe function. */
export const subscribeConnectionEvents = (listener: (event: ConnectionEvent) => void): (() => void) => {
   eventListeners.add(listener);
   return () => {
      eventListeners.delete(listener);
   };
};

export const stopConnection = async (): Promise<void> => {
   connectionParams = null;
   retryCount = 0;
   await teardown();
};

export const getConnectionState = (): signalR.HubConnectionState | null => {
   return connection?.state ?? null;
};

export const isConnectionUp = (): boolean => connection?.state === signalR.HubConnectionState.Connected;
